import { apiClient } from './api-client';
import type {
  MemberAvailability,
  CreateMemberAvailability,
  UpdateMemberAvailability,
  BulkAvailability,
  ScheduleException,
  CreateScheduleException,
  UpdateScheduleException,
  ScheduleTemplate,
  CreateScheduleTemplate,
  UpdateScheduleTemplate,
  OrganizationHoliday,
  CreateOrganizationHoliday,
  UpdateOrganizationHoliday,
  TeamSchedule,
  AvailabilityCheckRequest,
  AvailabilityCheckResult,
  TimeOffSummary,
} from '@scrambl/shared';

// =====================
// Member Availability API
// =====================

export const memberAvailabilityApi = {
  // Get availability for a member
  getByMember: async (
    memberId: string,
    effectiveDate?: string
  ): Promise<MemberAvailability[]> => {
    const params = new URLSearchParams();
    if (effectiveDate) params.append('effectiveDate', effectiveDate);

    const queryString = params.toString();
    return apiClient(
      `/member-resources/members/${memberId}/availability${queryString ? `?${queryString}` : ''}`,
      { method: 'GET' }
    );
  },

  // Get availability for all members in org
  getAll: async (filters?: {
    memberIds?: string[];
    department?: string;
    effectiveDate?: string;
  }): Promise<MemberAvailability[]> => {
    const params = new URLSearchParams();
    if (filters?.memberIds?.length) {
      params.append('memberIds', filters.memberIds.join(','));
    }
    if (filters?.department) params.append('department', filters.department);
    if (filters?.effectiveDate) params.append('effectiveDate', filters.effectiveDate);

    const queryString = params.toString();
    return apiClient(
      `/member-resources/availability${queryString ? `?${queryString}` : ''}`,
      { method: 'GET' }
    );
  },

  // Create single availability slot
  create: async (
    memberId: string,
    data: Omit<CreateMemberAvailability, 'memberId' | 'organizationId'>
  ): Promise<MemberAvailability> => {
    return apiClient(`/member-resources/members/${memberId}/availability`, {
      method: 'POST',
      body: data,
    });
  },

  // Update single availability slot
  update: async (
    availabilityId: string,
    data: UpdateMemberAvailability
  ): Promise<MemberAvailability> => {
    return apiClient(`/member-resources/availability/${availabilityId}`, {
      method: 'PUT',
      body: data,
    });
  },

  // Delete single availability slot
  delete: async (availabilityId: string): Promise<void> => {
    return apiClient(`/member-resources/availability/${availabilityId}`, {
      method: 'DELETE',
    });
  },

  // Bulk update availability (replace entire week)
  bulkUpdate: async (
    memberId: string,
    data: Omit<BulkAvailability, 'memberId'>
  ): Promise<MemberAvailability[]> => {
    return apiClient(`/member-resources/members/${memberId}/availability/bulk`, {
      method: 'POST',
      body: data,
    });
  },

  // Apply a schedule template to a member
  applyTemplate: async (
    memberId: string,
    templateId: string,
    effectiveFrom?: string,
    effectiveTo?: string
  ): Promise<MemberAvailability[]> => {
    return apiClient(`/member-resources/members/${memberId}/availability/apply-template`, {
      method: 'POST',
      body: { templateId, effectiveFrom, effectiveTo },
    });
  },

  // Calculate weekly hours for a member
  calculateWeeklyHours: async (
    memberId: string,
    effectiveDate?: string
  ): Promise<number> => {
    const params = new URLSearchParams();
    if (effectiveDate) params.append('effectiveDate', effectiveDate);

    const queryString = params.toString();
    return apiClient(
      `/member-resources/members/${memberId}/availability/weekly-hours${queryString ? `?${queryString}` : ''}`,
      { method: 'GET' }
    );
  },
};

// =====================
// Schedule Exception API (Time-off requests)
// =====================

export const scheduleExceptionApi = {
  // Get exceptions with filters
  getAll: async (filters?: {
    memberId?: string;
    memberIds?: string[];
    exceptionType?: string;
    status?: string;
    startDate?: string;
    endDate?: string;
  }): Promise<ScheduleException[]> => {
    const params = new URLSearchParams();
    if (filters?.memberId) params.append('memberId', filters.memberId);
    if (filters?.memberIds?.length) {
      params.append('memberIds', filters.memberIds.join(','));
    }
    if (filters?.exceptionType) params.append('exceptionType', filters.exceptionType);
    if (filters?.status) params.append('status', filters.status);
    if (filters?.startDate) params.append('startDate', filters.startDate);
    if (filters?.endDate) params.append('endDate', filters.endDate);

    const queryString = params.toString();
    return apiClient(
      `/member-resources/schedule/exceptions${queryString ? `?${queryString}` : ''}`,
      { method: 'GET' }
    );
  },

  // Get a single exception by ID
  getById: async (exceptionId: string): Promise<ScheduleException> => {
    return apiClient(`/member-resources/schedule/exceptions/${exceptionId}`, {
      method: 'GET',
    });
  },

  // Get pending requests (for approval)
  getPending: async (filters?: {
    department?: string;
    limit?: number;
  }): Promise<ScheduleException[]> => {
    const params = new URLSearchParams();
    if (filters?.department) params.append('department', filters.department);
    if (filters?.limit) params.append('limit', filters.limit.toString());

    const queryString = params.toString();
    return apiClient(
      `/member-resources/schedule/exceptions/pending${queryString ? `?${queryString}` : ''}`,
      { method: 'GET' }
    );
  },

  // Create a new time-off request
  create: async (
    data: Omit<CreateScheduleException, 'organizationId' | 'createdBy'>
  ): Promise<ScheduleException> => {
    return apiClient('/member-resources/schedule/exceptions', {
      method: 'POST',
      body: data,
    });
  },

  // Update an exception (only while pending)
  update: async (
    exceptionId: string,
    data: UpdateScheduleException
  ): Promise<ScheduleException> => {
    return apiClient(`/member-resources/schedule/exceptions/${exceptionId}`, {
      method: 'PUT',
      body: data,
    });
  },

  // Approve an exception
  approve: async (exceptionId: string): Promise<ScheduleException> => {
    return apiClient(`/member-resources/schedule/exceptions/${exceptionId}/approve`, {
      method: 'POST',
    });
  },

  // Reject an exception
  reject: async (
    exceptionId: string,
    rejectionReason?: string
  ): Promise<ScheduleException> => {
    return apiClient(`/member-resources/schedule/exceptions/${exceptionId}/reject`, {
      method: 'POST',
      body: { rejectionReason },
    });
  },

  // Cancel an exception (by the requester)
  cancel: async (exceptionId: string): Promise<ScheduleException> => {
    return apiClient(`/member-resources/schedule/exceptions/${exceptionId}/cancel`, {
      method: 'POST',
    });
  },

  // Soft delete an exception
  delete: async (exceptionId: string): Promise<void> => {
    return apiClient(`/member-resources/schedule/exceptions/${exceptionId}`, {
      method: 'DELETE',
    });
  },

  // Get time-off summary for a member
  getTimeOffSummary: async (
    memberId: string,
    year: number
  ): Promise<TimeOffSummary> => {
    return apiClient(
      `/member-resources/members/${memberId}/time-off-summary?year=${year}`,
      { method: 'GET' }
    );
  },
};

// =====================
// Schedule Template API
// =====================

export const scheduleTemplateApi = {
  // Get all templates
  getAll: async (filters?: {
    isActive?: boolean;
  }): Promise<ScheduleTemplate[]> => {
    const params = new URLSearchParams();
    if (filters?.isActive !== undefined) {
      params.append('isActive', filters.isActive.toString());
    }

    const queryString = params.toString();
    return apiClient(
      `/member-resources/schedule/templates${queryString ? `?${queryString}` : ''}`,
      { method: 'GET' }
    );
  },

  // Get a single template by ID
  getById: async (templateId: string): Promise<ScheduleTemplate> => {
    return apiClient(`/member-resources/schedule/templates/${templateId}`, {
      method: 'GET',
    });
  },

  // Create a new template
  create: async (
    data: Omit<CreateScheduleTemplate, 'organizationId' | 'createdBy'>
  ): Promise<ScheduleTemplate> => {
    return apiClient('/member-resources/schedule/templates', {
      method: 'POST',
      body: data,
    });
  },

  // Update a template
  update: async (
    templateId: string,
    data: UpdateScheduleTemplate
  ): Promise<ScheduleTemplate> => {
    return apiClient(`/member-resources/schedule/templates/${templateId}`, {
      method: 'PUT',
      body: data,
    });
  },

  // Delete a template
  delete: async (templateId: string): Promise<void> => {
    return apiClient(`/member-resources/schedule/templates/${templateId}`, {
      method: 'DELETE',
    });
  },
};

// =====================
// Organization Holiday API
// =====================

export const organizationHolidayApi = {
  // Get all holidays
  getAll: async (filters?: {
    year?: number;
    department?: string;
  }): Promise<OrganizationHoliday[]> => {
    const params = new URLSearchParams();
    if (filters?.year) params.append('year', filters.year.toString());
    if (filters?.department) params.append('department', filters.department);

    const queryString = params.toString();
    return apiClient(
      `/member-resources/schedule/holidays${queryString ? `?${queryString}` : ''}`,
      { method: 'GET' }
    );
  },

  // Get a single holiday by ID
  getById: async (holidayId: string): Promise<OrganizationHoliday> => {
    return apiClient(`/member-resources/schedule/holidays/${holidayId}`, {
      method: 'GET',
    });
  },

  // Create a new holiday
  create: async (
    data: Omit<CreateOrganizationHoliday, 'organizationId' | 'createdBy'>
  ): Promise<OrganizationHoliday> => {
    return apiClient('/member-resources/schedule/holidays', {
      method: 'POST',
      body: data,
    });
  },

  // Update a holiday
  update: async (
    holidayId: string,
    data: UpdateOrganizationHoliday
  ): Promise<OrganizationHoliday> => {
    return apiClient(`/member-resources/schedule/holidays/${holidayId}`, {
      method: 'PUT',
      body: data,
    });
  },

  // Delete a holiday
  delete: async (holidayId: string): Promise<void> => {
    return apiClient(`/member-resources/schedule/holidays/${holidayId}`, {
      method: 'DELETE',
    });
  },
};

// =====================
// Team Schedule API
// =====================

export const teamScheduleApi = {
  // Get team schedule for a date range
  getTeamSchedule: async (params: {
    startDate: string;
    endDate: string;
    memberIds?: string[];
    department?: string;
  }): Promise<TeamSchedule> => {
    const queryParams = new URLSearchParams();
    queryParams.append('startDate', params.startDate);
    queryParams.append('endDate', params.endDate);
    if (params.memberIds?.length) {
      queryParams.append('memberIds', params.memberIds.join(','));
    }
    if (params.department) queryParams.append('department', params.department);

    return apiClient(`/member-resources/schedule/team?${queryParams.toString()}`, {
      method: 'GET',
    });
  },

  // Get schedule summary statistics
  getScheduleSummary: async (params: {
    startDate: string;
    endDate: string;
    memberIds?: string[];
    department?: string;
  }): Promise<{
    totalMembers: number;
    totalAvailableHours: number;
    totalTimeOffHours: number;
    averageUtilization: number;
    memberSummaries: {
      memberId: string;
      memberName: string;
      availableHours: number;
      timeOffHours: number;
      holidays: number;
    }[];
  }> => {
    const queryParams = new URLSearchParams();
    queryParams.append('startDate', params.startDate);
    queryParams.append('endDate', params.endDate);
    if (params.memberIds?.length) {
      queryParams.append('memberIds', params.memberIds.join(','));
    }
    if (params.department) queryParams.append('department', params.department);

    return apiClient(`/member-resources/schedule/summary?${queryParams.toString()}`, {
      method: 'GET',
    });
  },

  // Check availability for members
  checkAvailability: async (
    request: AvailabilityCheckRequest
  ): Promise<AvailabilityCheckResult[]> => {
    return apiClient('/member-resources/schedule/check-availability', {
      method: 'POST',
      body: request,
    });
  },
};

// =====================
// Combined Schedule API
// =====================

export const scheduleApi = {
  availability: memberAvailabilityApi,
  exceptions: scheduleExceptionApi,
  templates: scheduleTemplateApi,
  holidays: organizationHolidayApi,
  team: teamScheduleApi,
};
